import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

import './assets/css/main.scss';

import { Security, useSecurity } from '@sms/security-plasma-security-auth-sdk-react';

import { App, Result, ThemeDimensions, User } from '@sms/plasma-ui';
import logoDarkBg from './assets/img/journal-bg-dark.svg';
import logoLightBg from './assets/img/journal-bg-light.svg';
import app from './config/app.json';
import translations from './config/i18n';
import pages from './pages';
import CentralizedTextPage from './components/CentralizedTextPage/CentralizedTextPage';

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
const SUB_PATH: string = (window as any).plasma?.environment?.SUB_PATH ?? '/';
const PROCESSED_SUB_PATH = SUB_PATH.replace(/\/$/, '');


const MainApp: FunctionComponent = () => {
  const security = useSecurity();

  // Force theme to 'light' and prevent any change
  useEffect(() => {
    const forcedTheme = 'light';
    localStorage.setItem('theme', forcedTheme);
    // Prevent any code from changing the theme
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      if (key === 'theme' && value !== forcedTheme) {
        // Ignore attempts to change theme
        return;
      }
      return originalSetItem.apply(this, [...arguments] as [string, string]);
    };
    // Also prevent removal
    const originalRemoveItem = localStorage.removeItem;
    localStorage.removeItem = function (key) {
      if (key === 'theme') {
        // Ignore attempts to remove theme
        return;
      }
      return originalRemoveItem.apply(this, [...arguments] as [string]);
    };
  }, []);

  const [hasTriedSignin, setHasTriedSignin] = React.useState(false);

  const handleLanguageChange = useCallback((language: string) => {
    localStorage.setItem('@sms:language', language);
  }, []);

  const handleLogout = useCallback(() => {
    security.signoutRedirect();
  }, []);

  const language = localStorage.getItem('@sms:language') || undefined;

  //temporary fix to hide header and menu
  const searchParams = new URLSearchParams(window.location.search);
  const hideNavigation = searchParams.get('hideNavigation') === 'true';

  useEffect(() => {
    if (hideNavigation) {
      document.body.classList.add('hide-navigation');
    } else {
      document.body.classList.remove('hide-navigation');
    }
  }, [hideNavigation]);
  //end temporary fix

  const handleThemeChange = useCallback((theme: ThemeDimensions) => {
    localStorage.setItem('@sms:theme', JSON.stringify(theme));
  }, []);

  const user = useMemo(
    () =>
      ({
        id: security.user?.profile.sub,
        firstName: security.user?.profile.given_name,
        lastName: security.user?.profile.family_name,
        email: security.user?.profile.email,
        username: security.user?.profile.preferred_username,
      }) as User,
    [security.user],
  );

  const theme = useMemo(() => {
    const themeString = localStorage.getItem('@sms:theme');
    return themeString ? (JSON.parse(themeString) as ThemeDimensions) : undefined;
  }, []);

  // automatically sign-in
  React.useEffect(() => {
    if (!security.isAuthenticated && !security.activeNavigator && !security.isLoading && !hasTriedSignin) {
      security.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [security, hasTriedSignin]);

  if (!security.isAuthenticated && security.isLoading)
    return <CentralizedTextPage textKey="Signing you in/out of the application..." />;

  if (!security.isAuthenticated) return <CentralizedTextPage textKey="Unable to log in" />;

  if (!security.permissions.size && security.isLoading) return <CentralizedTextPage textKey="Getting permissions..." />;

  return (
    <App
      code={app.code}
      isAuthenticated
      user={user}
      routerConfig={{ pages, launchpadRoutePath: `${PROCESSED_SUB_PATH}/${app.code}` }}
      headerConfig={{ logo: logoDarkBg, title: app.title }}
      loadingConfig={{ icon: logoLightBg }}
      profileAvatarConfig={{ onLogout: handleLogout }}
      language={language}
      onLanguageChange={handleLanguageChange}
      theme={theme}
      onThemeChange={handleThemeChange}
      translations={translations}
      hasAuthorization={(event) => {
        return security.hasAuthorization(event);
      }}
      permissions={Array.from(security.permissions)}
    />
  );
};

ReactDOM.render(
  <Security
    unauthorizedRedirectPath={`${PROCESSED_SUB_PATH}/login`}
    unauthorizedAppComponent={<Result status="403" />}
    unauthorizedRouteComponent={<Result status="403" />}
  >
    <MainApp />
  </Security>,
  document.getElementById('root'),
);
